import { Component, AuthUser, Project, Bookmark } from '../types';
import { authService } from './auth';

class BookmarkService {
  private static instance: BookmarkService | null = null;
  private bookmarks: Bookmark[] = [];
  private initialized = false;
  private bookmarkCache: Map<string, boolean> = new Map(); // Cache for bookmark status
  private lastSyncTime: number = 0;
  private readonly SYNC_COOLDOWN = 5 * 60 * 1000; // 5 minutes between syncs

  constructor() {
    // Don't initialize immediately to avoid circular dependency
  }

  static getInstance(): BookmarkService {
    if (!BookmarkService.instance) {
      BookmarkService.instance = new BookmarkService();
    }
    return BookmarkService.instance;
  }

  private async ensureInitialized(): Promise<void> {
    if (!this.initialized) {
      await this.loadBookmarks();
      this.initialized = true;
      this.updateBookmarkCache();
    }
  }

  private getStorageKey(): string {
    const user = authService.getCurrentUser();
    return user ? `bookmarks_${user.id}` : 'bookmarks_guest';
  }

  private updateBookmarkCache(): void {
    this.bookmarkCache.clear();
    const user = authService.getCurrentUser();
    if (user) {
      this.bookmarks.forEach(bookmark => {
        if (bookmark.userId === user.id) {
          this.bookmarkCache.set(bookmark.componentName, true);
        }
      });
    }
  }

  private async loadBookmarks(): Promise<void> {
    try {
      // Try to load from Google Drive first (but only if we haven't synced recently)
      if (authService.isAuthenticated() && (Date.now() - this.lastSyncTime) > this.SYNC_COOLDOWN) {
        try {
          const { googleDriveService } = await import('./googleDrive');
          const hasDrivePermissions = await googleDriveService.checkDrivePermissions();
          if (hasDrivePermissions) {
            const driveBookmarks = await googleDriveService.loadBookmarks();
            if (driveBookmarks.length > 0) {
              this.bookmarks = driveBookmarks;
              this.lastSyncTime = Date.now();
              return;
            }
          }
        } catch (error) {
          // Google Drive not available, falling back to localStorage
          if (error instanceof Error && error.message.includes('expired')) {
            console.warn('Google Drive access token expired. Falling back to localStorage. Please sign in again to sync with Google Drive.');
          } else {
            console.warn('Google Drive not available. Falling back to localStorage.');
          }
        }
      }

      // Fallback to localStorage
      const saved = localStorage.getItem(this.getStorageKey());
      if (saved) {
        this.bookmarks = JSON.parse(saved);
        
        // Migrate existing bookmarks to new minimal format
        let needsUpdate = false;
        this.bookmarks = this.bookmarks.map(bookmark => {
          // If bookmark has old format, migrate to new format
          if ('projectName' in bookmark || 'componentType' in bookmark || 'createdAt' in bookmark) {
            needsUpdate = true;
            return {
              id: bookmark.id,
              userId: bookmark.userId,
              componentName: bookmark.componentName,
              projectId: bookmark.projectId
            };
          }
          return bookmark;
        });
        
        if (needsUpdate) {
          this.saveBookmarks();
        }
      } else {
        // No saved bookmarks, ensure we have an empty array
        this.bookmarks = [];
      }
    } catch (error) {
      console.error('Error loading bookmarks:', error);
      this.bookmarks = [];
    }
  }

  private async saveBookmarks(): Promise<void> {
    try {
      // Save to localStorage first for immediate persistence
      localStorage.setItem(this.getStorageKey(), JSON.stringify(this.bookmarks));
      
      // Try to save to Google Drive in the background (but don't block)
      if (authService.isAuthenticated()) {
        // Use setTimeout to make this non-blocking
        setTimeout(async () => {
          try {
            const { googleDriveService } = await import('./googleDrive');
            const hasDrivePermissions = await googleDriveService.checkDrivePermissions();
            if (hasDrivePermissions) {
              await googleDriveService.saveBookmarks(this.bookmarks);
              this.lastSyncTime = Date.now();
            }
          } catch (error) {
            console.warn('Background Google Drive sync failed:', error);
          }
        }, 0);
      }
    } catch (error) {
      console.error('Error saving bookmarks:', error);
    }
  }

  async addBookmark(component: Component, project?: Project): Promise<Bookmark> {
    await this.ensureInitialized();
    
    const user = authService.getCurrentUser();
    if (!user) {
      throw new Error('User must be authenticated to add bookmarks');
    }

    // Ensure we have project ID
    const projectId = project?.id || component.projectId;
    
    if (!projectId) {
      throw new Error('Component must belong to a project to be bookmarked');
    }

    // Check if bookmark already exists
    const existingIndex = this.bookmarks.findIndex(
      bookmark => bookmark.componentName === component.name && bookmark.userId === user.id
    );

    if (existingIndex !== -1) {
      // Update existing bookmark with new project ID if different
      this.bookmarks[existingIndex] = {
        ...this.bookmarks[existingIndex],
        projectId: projectId
      };
    } else {
      // Create new bookmark with minimal data
      const bookmark: Bookmark = {
        id: `${user.id}_${component.name}_${Date.now()}`,
        userId: user.id,
        componentName: component.name,
        projectId: projectId
      };
      this.bookmarks.push(bookmark);
    }

    // Update cache immediately
    this.bookmarkCache.set(component.name, true);
    
    await this.saveBookmarks();
    
    return this.bookmarks.find(
      bookmark => bookmark.componentName === component.name && bookmark.userId === user.id
    )!;
  }

  async removeBookmark(componentName: string): Promise<void> {
    await this.ensureInitialized();
    
    const user = authService.getCurrentUser();
    if (!user) {
      throw new Error('User must be authenticated to remove bookmarks');
    }

    this.bookmarks = this.bookmarks.filter(
      bookmark => !(bookmark.componentName === componentName && bookmark.userId === user.id)
    );
    
    // Update cache immediately
    this.bookmarkCache.set(componentName, false);
    
    await this.saveBookmarks();
  }

  async getBookmarks(): Promise<Bookmark[]> {
    await this.ensureInitialized();
    
    const user = authService.getCurrentUser();
    if (!user) {
      return [];
    }

    return this.bookmarks.filter(bookmark => bookmark.userId === user.id);
  }

  // Fast cached check - no API calls!
  async isBookmarked(componentName: string): Promise<boolean> {
    await this.ensureInitialized();
    
    const user = authService.getCurrentUser();
    if (!user) {
      return false;
    }

    // Use cache first
    if (this.bookmarkCache.has(componentName)) {
      return this.bookmarkCache.get(componentName)!;
    }

    // Fallback to checking the bookmarks array (but don't make API calls)
    const isBookmarked = this.bookmarks.some(
      bookmark => bookmark.componentName === componentName && bookmark.userId === user.id
    );
    
    // Cache the result
    this.bookmarkCache.set(componentName, isBookmarked);
    
    return isBookmarked;
  }

  // Get bookmarked components with full component data
  async getBookmarkedComponents(allComponents: Component[]): Promise<Component[]> {
    await this.ensureInitialized();
    
    const bookmarks = await this.getBookmarks();
    const bookmarkedNames = new Set(bookmarks.map(b => b.componentName));
    
    return allComponents.filter(component => bookmarkedNames.has(component.name));
  }

  // Sync local bookmarks to Google Drive when user first signs in
  async syncToGoogleDrive(): Promise<void> {
    if (!authService.isAuthenticated()) {
      return;
    }

    try {
      const { googleDriveService } = await import('./googleDrive');
      const hasDrivePermissions = await googleDriveService.checkDrivePermissions();
      if (hasDrivePermissions) {
        // Check if we have local bookmarks that aren't in Drive
        const localBookmarks = localStorage.getItem(this.getStorageKey());
        if (localBookmarks) {
          const localData = JSON.parse(localBookmarks);
          if (localData.length > 0) {
            // Merge with existing Drive bookmarks
            const driveBookmarks = await googleDriveService.loadBookmarks();
            const mergedBookmarks = [...driveBookmarks, ...localData];
            
            // Remove duplicates based on componentName and userId
            const uniqueBookmarks = mergedBookmarks.filter((bookmark, index, self) => 
              index === self.findIndex(b => 
                b.componentName === bookmark.componentName && b.userId === bookmark.userId
              )
            );
            
            await googleDriveService.saveBookmarks(uniqueBookmarks);
            this.bookmarks = uniqueBookmarks;
            this.updateBookmarkCache();
            this.lastSyncTime = Date.now();
            console.log('Synced local bookmarks to Google Drive');
          }
        }
      }
    } catch (error) {
      console.error('Error syncing bookmarks to Google Drive:', error);
    }
  }

  // Force refresh from Google Drive (for manual sync)
  async forceRefreshFromDrive(): Promise<void> {
    if (!authService.isAuthenticated()) {
      return;
    }

    try {
      const { googleDriveService } = await import('./googleDrive');
      const hasDrivePermissions = await googleDriveService.checkDrivePermissions();
      if (hasDrivePermissions) {
        const driveBookmarks = await googleDriveService.loadBookmarks();
        this.bookmarks = driveBookmarks;
        this.updateBookmarkCache();
        this.lastSyncTime = Date.now();
        console.log('Forced refresh from Google Drive');
      }
    } catch (error) {
      console.error('Error forcing refresh from Google Drive:', error);
    }
  }

  // Clear all bookmarks and localStorage data
  async clearAllBookmarks(): Promise<void> {
    await this.ensureInitialized();
    
    const user = authService.getCurrentUser();
    if (!user) {
      throw new Error('User must be authenticated to clear bookmarks');
    }

    // Clear from memory
    this.bookmarks = [];
    
    // Clear cache
    this.bookmarkCache.clear();
    
    // Clear from localStorage
    localStorage.removeItem(this.getStorageKey());
    
    // Clear from Google Drive if available
    try {
      const { googleDriveService } = await import('./googleDrive');
      const hasDrivePermissions = await googleDriveService.checkDrivePermissions();
      if (hasDrivePermissions) {
        await googleDriveService.saveBookmarks([]);
      }
    } catch (error) {
      console.warn('Could not clear bookmarks from Google Drive:', error);
    }
    
    console.log('All bookmarks cleared successfully');
  }

  // Clean up stale bookmarks (bookmarks for components that no longer exist)
  async cleanupStaleBookmarks(allComponents: Component[]): Promise<void> {
    await this.ensureInitialized();
    
    const user = authService.getCurrentUser();
    if (!user) {
      return;
    }

    // Create a map of valid components by name and project ID for more accurate matching
    const validComponents = new Map<string, Component>();
    allComponents.forEach(component => {
      const key = `${component.name}-${component.projectId}`;
      validComponents.set(key, component);
      // Also store by name only for fallback
      if (!validComponents.has(component.name)) {
        validComponents.set(component.name, component);
      }
    });
    
    const originalCount = this.bookmarks.length;
    this.bookmarks = this.bookmarks.filter(bookmark => {
      if (bookmark.userId !== user.id) {
        return true; // Keep bookmarks from other users
      }
      
      // Check for exact match by name and project ID
      const exactKey = `${bookmark.componentName}-${bookmark.projectId}`;
      if (validComponents.has(exactKey)) {
        return true;
      }
      
      // Check for name-only match
      if (validComponents.has(bookmark.componentName)) {
        return true;
      }
      
      // If no match found, remove the bookmark
              console.log(`Removing stale bookmark for component: ${bookmark.componentName} in project ID: ${bookmark.projectId}`);
      return false;
    });

    if (this.bookmarks.length !== originalCount) {
      await this.saveBookmarks();
      this.updateBookmarkCache();
      console.log(`Cleaned up ${originalCount - this.bookmarks.length} stale bookmarks`);
    }
  }
}

export const bookmarkService = BookmarkService.getInstance();
